import React, { useState, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './AuthContext';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import SetupPage from './pages/SetupPage';
import GamePage from './pages/GamePage';
import ResultsPage from './pages/ResultsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import PricingPage from './pages/PricingPage';
import PracticeSetupPage from './pages/PracticeSetupPage';
import PracticePage from './pages/PracticePage';
import DashboardStatsPage from './pages/DashboardStatsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ClassesPage from './pages/ClassesPage';
import ClassDetailPage from './pages/ClassDetailPage';
import TournamentsPage from './pages/TournamentsPage';
import TournamentBracketPage from './pages/TournamentBracketPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AffiliatePage from './pages/AffiliatePage';
import CertificatePage from './pages/CertificatePage';
import CustomQuestionsPage from './pages/CustomQuestionsPage';
import AdminPage from './pages/AdminPage';
import ShopPage from './pages/ShopPage';
import GuidePage from './pages/GuidePage';
import RewardsPage from './pages/RewardsPage';
import SoloGamePage from './pages/SoloGamePage';
import ClassroomPage from './pages/ClassroomPage';
import SchoolTeachersPage from './pages/SchoolTeachersPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import './landing.css';

// Code-split SEO pages for better performance
const QuizLandingPage = lazy(() => import('./pages/QuizLandingPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogWhyChildHatesMath = lazy(() => import('./pages/BlogWhyChildHatesMath'));
const BlogTuisyenVsApp = lazy(() => import('./pages/BlogTuisyenVsApp'));
const BlogCaraBuatAnakSuka = lazy(() => import('./pages/BlogCaraBuatAnakSuka'));
const BlogHafalSifir = lazy(() => import('./pages/BlogHafalSifir'));
const BlogPersediaanUASA = lazy(() => import('./pages/BlogPersediaanUASA'));
const BlogTeknikKBAT = lazy(() => import('./pages/BlogTeknikKBAT'));
const BlogBezaKSSR = lazy(() => import('./pages/BlogBezaKSSR'));
const BlogScoringASPM = lazy(() => import('./pages/BlogScoringASPM'));
const BlogGamePendidikan = lazy(() => import('./pages/BlogGamePendidikan'));
const BlogJadualSPM = lazy(() => import('./pages/BlogJadualSPM'));
const PetSelectionPage = lazy(() => import('./pages/PetSelectionPage'));
const PetPage = lazy(() => import('./pages/PetPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));

export const LanguageContext = createContext();

export function useLanguage() {
  return useContext(LanguageContext);
}

function HomeRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <HomePage /> : <LandingPage />;
}

function App() {
  const [language, setLanguage] = useState('bm');

  return (
    <HelmetProvider>
    <LanguageContext.Provider value={{ language, setLanguage }}>
      <AuthProvider>
        <ErrorBoundary>
        <BrowserRouter>
          <div className="app">
            <Suspense fallback={<div style={{ background: '#020617', minHeight: '100vh' }} />}>
            <Routes>
              <Route path="/" element={<HomeRouter />} />
              <Route path="/setup" element={<SetupPage />} />
              <Route path="/game" element={<GamePage />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/practice-setup" element={<PracticeSetupPage />} />
              <Route path="/practice" element={<PracticePage />} />
              <Route path="/progress" element={<DashboardStatsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth-callback" element={<AuthCallbackPage />} />
              <Route path="/classes" element={<ClassesPage />} />
              <Route path="/classes/:id" element={<ClassDetailPage />} />
              <Route path="/tournaments" element={<TournamentsPage />} />
              <Route path="/tournaments/:id" element={<TournamentBracketPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/affiliate" element={<AffiliatePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/certificate" element={<CertificatePage />} />
              <Route path="/custom-questions" element={<CustomQuestionsPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/guide" element={<GuidePage />} />
              <Route path="/rewards" element={<RewardsPage />} />
              <Route path="/solo-game" element={<SoloGamePage />} />
              <Route path="/classroom" element={<ClassroomPage />} />
              <Route path="/school-teachers" element={<SchoolTeachersPage />} />
              <Route path="/pet-select" element={<PetSelectionPage />} />
              <Route path="/pet" element={<PetPage />} />
              <Route path="/settings/delete-account" element={<DeleteAccountPage />} />
              <Route path="/kuiz/:subject/:year" element={<QuizLandingPage />} />
              <Route path="/kuiz/:subject" element={<QuizLandingPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/why-your-child-hates-math" element={<BlogWhyChildHatesMath />} />
              <Route path="/blog/tuisyen-vs-app-belajar" element={<BlogTuisyenVsApp />} />
              <Route path="/blog/cara-buat-anak-suka-belajar" element={<BlogCaraBuatAnakSuka />} />
              <Route path="/blog/cara-hafal-sifir" element={<BlogHafalSifir />} />
              <Route path="/blog/persediaan-uasa-2026" element={<BlogPersediaanUASA />} />
              <Route path="/blog/teknik-kbat-matematik" element={<BlogTeknikKBAT />} />
              <Route path="/blog/beza-kssr-kssm" element={<BlogBezaKSSR />} />
              <Route path="/blog/cara-scoring-spm-matematik" element={<BlogScoringASPM />} />
              <Route path="/blog/game-pendidikan-terbaik-malaysia" element={<BlogGamePendidikan />} />
              <Route path="/blog/jadual-belajar-spm-2026" element={<BlogJadualSPM />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </div>
        </BrowserRouter>
        </ErrorBoundary>
      </AuthProvider>
    </LanguageContext.Provider>
    </HelmetProvider>
  );
}

export default App;
